/**
 * Editor Store - manages editor UI state
 */

import { defineStore } from 'pinia';
import { ref, computed } from 'vue';

/** Available editing tools */
export enum EditorTool {
    SELECT = 'select',
    PAINT_TILE = 'paint_tile',
    PAINT_OBJECT = 'paint_object',
    ERASE_OBJECT = 'erase_object',
    EYEDROPPER = 'eyedropper',
    BUCKET_FILL = 'bucket_fill',
    TOGGLE_BLOCKED = 'toggle_blocked',
    TOGGLE_TELEPORT = 'toggle_teleport',
}

/** Brush shape */
export enum BrushShape {
    SQUARE = 'square',
    CIRCLE = 'circle',
}

/** Selection rectangle */
export interface Selection {
    x1: number;
    y1: number;
    x2: number;
    y2: number;
}

/** A tile in the multi-tile brush */
export interface BrushTile {
    offsetX: number;  // relative X from brush origin
    offsetY: number;  // relative Y from brush origin
    spriteId: number;
    frame: number;
}

export const useEditorStore = defineStore('editor', () => {
    // Tool state
    const currentTool = ref<EditorTool>(EditorTool.PAINT_TILE);
    const brushSize = ref(1);
    const brushShape = ref<BrushShape>(BrushShape.SQUARE);

    // Selected tile/object for painting (single tile mode, kept for backwards compatibility)
    const selectedTileSprite = ref(0);
    const selectedTileSpriteFrame = ref(0);
    const selectedObjectSprite = ref(200);
    const selectedObjectSpriteFrame = ref(0);

    // Multi-tile brush
    const tileBrush = ref<BrushTile[]>([]);
    const objectBrush = ref<BrushTile[]>([]);

    // View state
    const showGrid = ref(true);
    const showObjects = ref(true);
    const showBlocked = ref(false);
    const showTeleports = ref(false);
    const showFarming = ref(false);
    const showBuildable = ref(false);
    const showWater = ref(false);
    const zoom = ref(1);
    const panX = ref(0);
    const panY = ref(0);

    // Selection
    const selection = ref<Selection | null>(null);
    const isSelecting = ref(false);

    // Mouse state
    const mouseX = ref(0);
    const mouseY = ref(0);
    const tileX = ref(0);
    const tileY = ref(0);
    const isMouseDown = ref(false);

    // Panel visibility
    const showTilePalette = ref(true);
    const showObjectPalette = ref(true);
    const showProperties = ref(true);
    const showMinimap = ref(true);

    // Palette selection state (shared between TilePalette and SpritesheetViewer)
    const paletteTab = ref<'tiles' | 'objects'>('tiles');
    const selectedPak = ref<string | null>(null);
    const selectedPaletteSpriteId = ref<number | null>(null);

    // Computed
    const isPaintingTool = computed(() =>
        [EditorTool.PAINT_TILE, EditorTool.PAINT_OBJECT, EditorTool.BUCKET_FILL].includes(
            currentTool.value
        )
    );

    const isErasingTool = computed(() =>
        currentTool.value === EditorTool.ERASE_OBJECT
    );

    const hasSelection = computed(() => selection.value !== null);

    const selectionBounds = computed(() => {
        if (!selection.value) return null;
        const { x1, y1, x2, y2 } = selection.value;
        return {
            left: Math.min(x1, x2),
            top: Math.min(y1, y2),
            right: Math.max(x1, x2),
            bottom: Math.max(y1, y2),
            width: Math.abs(x2 - x1) + 1,
            height: Math.abs(y2 - y1) + 1,
        };
    });

    // Brush bounds (for cursor preview)
    const tileBrushBounds = computed(() => {
        if (tileBrush.value.length === 0) return { width: 1, height: 1 };
        const minX = Math.min(...tileBrush.value.map(t => t.offsetX));
        const maxX = Math.max(...tileBrush.value.map(t => t.offsetX));
        const minY = Math.min(...tileBrush.value.map(t => t.offsetY));
        const maxY = Math.max(...tileBrush.value.map(t => t.offsetY));
        return {
            width: maxX - minX + 1,
            height: maxY - minY + 1,
        };
    });

    const objectBrushBounds = computed(() => {
        if (objectBrush.value.length === 0) return { width: 1, height: 1 };
        const minX = Math.min(...objectBrush.value.map(t => t.offsetX));
        const maxX = Math.max(...objectBrush.value.map(t => t.offsetX));
        const minY = Math.min(...objectBrush.value.map(t => t.offsetY));
        const maxY = Math.max(...objectBrush.value.map(t => t.offsetY));
        return {
            width: maxX - minX + 1,
            height: maxY - minY + 1,
        };
    });

    // Actions
    function setTool(tool: EditorTool): void {
        currentTool.value = tool;
    }

    function setBrushSize(size: number): void {
        brushSize.value = Math.max(1, Math.min(10, size));
    }

    function selectTile(sprite: number, frame: number): void {
        selectedTileSprite.value = sprite;
        selectedTileSpriteFrame.value = frame;
    }

    function selectObject(sprite: number, frame: number): void {
        selectedObjectSprite.value = sprite;
        selectedObjectSpriteFrame.value = frame;
    }

    /** Toggle a tile in the multi-tile brush */
    function toggleTileBrush(offsetX: number, offsetY: number, spriteId: number, frame: number): void {
        // Look up by spriteId and frame since offsets change after normalization
        const index = tileBrush.value.findIndex(
            t => t.spriteId === spriteId && t.frame === frame
        );
        if (index >= 0) {
            // Remove if exists
            tileBrush.value.splice(index, 1);
        } else {
            // Add new tile
            tileBrush.value.push({ offsetX, offsetY, spriteId, frame });
        }
        // Normalize offsets to start from 0,0
        normalizeBrush(tileBrush.value);
    }

    /** Toggle an object in the multi-tile brush */
    function toggleObjectBrush(offsetX: number, offsetY: number, spriteId: number, frame: number): void {
        // Look up by spriteId and frame since offsets change after normalization
        const index = objectBrush.value.findIndex(
            t => t.spriteId === spriteId && t.frame === frame
        );
        if (index >= 0) {
            objectBrush.value.splice(index, 1);
        } else {
            objectBrush.value.push({ offsetX, offsetY, spriteId, frame });
        }
        normalizeBrush(objectBrush.value);
    }

    /** Normalize brush offsets so minimum is 0,0 */
    function normalizeBrush(brush: BrushTile[]): void {
        if (brush.length === 0) return;
        const minX = Math.min(...brush.map(t => t.offsetX));
        const minY = Math.min(...brush.map(t => t.offsetY));
        for (const tile of brush) {
            tile.offsetX -= minX;
            tile.offsetY -= minY;
        }
    }

    /** Clear the tile brush */
    function clearTileBrush(): void {
        tileBrush.value = [];
    }

    /** Clear the object brush */
    function clearObjectBrush(): void {
        objectBrush.value = [];
    }

    /** Check if a tile is in the brush by sprite ID and frame */
    function isTileInBrush(spriteId: number, frame: number): boolean {
        return tileBrush.value.some(t => t.spriteId === spriteId && t.frame === frame);
    }

    /** Check if an object is in the brush by sprite ID and frame */
    function isObjectInBrush(spriteId: number, frame: number): boolean {
        return objectBrush.value.some(t => t.spriteId === spriteId && t.frame === frame);
    }

    function setZoom(newZoom: number): void {
        zoom.value = Math.max(0.25, Math.min(4, newZoom));
    }

    function zoomIn(): void {
        setZoom(zoom.value * 1.25);
    }

    function zoomOut(): void {
        setZoom(zoom.value / 1.25);
    }

    function resetView(): void {
        zoom.value = 1;
        panX.value = 0;
        panY.value = 0;
    }

    function pan(dx: number, dy: number): void {
        panX.value += dx;
        panY.value += dy;
    }

    function startSelection(x: number, y: number): void {
        selection.value = { x1: x, y1: y, x2: x, y2: y };
        isSelecting.value = true;
    }

    function updateSelection(x: number, y: number): void {
        if (!selection.value) return;
        selection.value = { ...selection.value, x2: x, y2: y };
    }

    function endSelection(): void {
        isSelecting.value = false;
    }

    function clearSelection(): void {
        selection.value = null;
        isSelecting.value = false;
    }

    function updateMousePosition(screenX: number, screenY: number, tileXPos: number, tileYPos: number): void {
        mouseX.value = screenX;
        mouseY.value = screenY;
        tileX.value = tileXPos;
        tileY.value = tileYPos;
    }

    function togglePanel(panel: 'tilePalette' | 'objectPalette' | 'properties' | 'minimap'): void {
        switch (panel) {
            case 'tilePalette':
                showTilePalette.value = !showTilePalette.value;
                break;
            case 'objectPalette':
                showObjectPalette.value = !showObjectPalette.value;
                break;
            case 'properties':
                showProperties.value = !showProperties.value;
                break;
            case 'minimap':
                showMinimap.value = !showMinimap.value;
                break;
        }
    }

    return {
        // Tool state
        currentTool,
        brushSize,
        brushShape,
        selectedTileSprite,
        selectedTileSpriteFrame,
        selectedObjectSprite,
        selectedObjectSpriteFrame,
        // Multi-tile brush
        tileBrush,
        objectBrush,
        // View state
        showGrid,
        showObjects,
        showBlocked,
        showTeleports,
        showFarming,
        showBuildable,
        showWater,
        zoom,
        panX,
        panY,
        // Selection
        selection,
        isSelecting,
        // Mouse
        mouseX,
        mouseY,
        tileX,
        tileY,
        isMouseDown,
        // Panels
        showTilePalette,
        showObjectPalette,
        showProperties,
        showMinimap,
        // Palette selection
        paletteTab,
        selectedPak,
        selectedPaletteSpriteId,
        // Computed
        isPaintingTool,
        isErasingTool,
        hasSelection,
        selectionBounds,
        tileBrushBounds,
        objectBrushBounds,
        // Actions
        setTool,
        setBrushSize,
        selectTile,
        selectObject,
        toggleTileBrush,
        toggleObjectBrush,
        clearTileBrush,
        clearObjectBrush,
        isTileInBrush,
        isObjectInBrush,
        setZoom,
        zoomIn,
        zoomOut,
        resetView,
        pan,
        startSelection,
        updateSelection,
        endSelection,
        clearSelection,
        updateMousePosition,
        togglePanel,
    };
});
